import { Component, Input, OnInit, OnChanges, OnDestroy, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Observable, Subject } from 'rxjs';
import { takeUntil, distinctUntilChanged } from 'rxjs/operators';

import { DataService } from '@/services/data.service';
import { BidTagsService } from '@/services/bid-tags.service';
import { Web3Service } from '@/services/web3.service';
import { StateService } from '@/services/state.service';
import { ThemeService } from '@/services/theme.service';
import { WalletAddressDirective } from '@/directives/wallet-address.directive';
import { TippyDirective } from '@/directives/tippy.directive';
import { AddressAvatarComponent } from '@/components/address-avatar/address-avatar.component';
import { BidDetailsModalComponent, StashBid as BidDetailsStashBid } from '@/components/bid-details-modal/bid-details-modal.component';
import { TokenIdParsePipe } from '@/pipes/token-id-parse.pipe';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { environment } from 'src/environments/environment';
import { keccak256, stringToBytes } from 'viem';
import { normalizeStashFundedForUi } from '@/utils/stash-funded';
import { OnChainBidInfoIconComponent } from '@/components/on-chain-bid-info-icon/on-chain-bid-info-icon.component';
import {
  cloneBidLabel,
  nativeBidTypeLabel,
  onChainBidPlatformTooltip,
  onChainBidTypeLabel,
  onChainBidTypeLabelTitle,
} from '@/utils/native-market-bid-labels';
import { PunksmarketIndexerBid } from '@/models/unified-market-bid';
import { classifyPunksmarketIndexerBid } from '@/utils/punksmarket-criteria.util';

export interface BidFilterState {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  filterStatus: string;
  filterSource: string;
  filterBidder: string;
}

export interface StashBid {
  id: string;
  bidder_address: string;
  punk_indices?: string;
  punk_indices_count?: number;
  punkIndices?: number[];
  selected_punk_ids?: number[] | string;
  tag?: string;
  tags?: any;
  bid_amount_wei: string;
  bid_amount_eth: number;
  merkle_root?: string;
  account_nonce?: number;
  bid_nonce?: number;
  status?: string;
  created_at: string;
  expires_at?: string;
  signature?: string;
  source?: string;
  decodedTags?: string[];
  bidType?: 'trait' | 'collection' | 'custom' | 'owner' | 'legacy' | 'punksmarket';
  bidTypeLabel?: string;
  stashAddress?: string;
  ownerAddress?: string;
  confidential?: boolean;
  /** 0 = underfunded (show pill), 1 = funded; normalized in processBids. */
  stash_funded?: 0 | 1;
  biddingOn?: string;
  firstPunkId?: number;
  matched_punk_id?: number;
  punkIds?: number[];
  matchingOwnedPunks?: number[];
  isLegacyBid?: boolean;
  legacyBid?: any;
  log?: string | null;
  /** Pre-computed platform icon path (avoids repeated calls in template). */
  _platformIcon?: string | null;
  /** Pre-computed platform tooltip text (avoids repeated calls in template). */
  _platformTooltip?: string | null;
  /** UI label for status column (Active, Expired, Cancelled, etc.). */
  uiStatusLabel?: string;
  /** UI kind for status pill styling (active, expired, cancelled). */
  uiStatusKind?: 'active' | 'expired' | 'cancelled' | '';
  /** Punks rendered in the table frise, precomputed once in processBids. */
  displayPunkIds?: number[];
  /** Precomputed in processBids — clone is stash-only, not on-chain / legacy. */
  showCloneBidButton?: boolean;
  /** Unified bid platform when merged with PunksMarket bids. */
  platform?: 'stash' | 'punksmarket' | 'legacy';
  punksmarketBidId?: string;
}

@Component({
  selector: 'app-stash-bids-table',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    NgSelectModule,
    WalletAddressDirective,
    TippyDirective,
    AddressAvatarComponent,
    BidDetailsModalComponent,
    TokenIdParsePipe,
    LazyLoadImageModule,
    OnChainBidInfoIconComponent,
  ],
  templateUrl: './stash-bids-table.component.html',
  styleUrls: ['./stash-bids-table.component.scss']
})
export class StashBidsTableComponent implements OnInit, OnChanges, OnDestroy {
  readonly nativeBidTypeLabel = nativeBidTypeLabel;
  readonly onChainBidTypeLabel = onChainBidTypeLabel;
  readonly cloneBidLabel = cloneBidLabel;
  @Input() bids: StashBid[] = [];
  @Input() showFilters: boolean = false;
  /** When true, skip client-side sorting and preserve the order returned by the server. */
  @Input() preserveServerOrder: boolean = false;
  @Input() showPagination: boolean = true;
  @Input() itemsPerPage: number = 50;
  @Input() showAcceptButton: boolean = false;
  @Input() showCancelButton: boolean = false;
  @Input() showPunksColumn: boolean = false;
  /**
   * Controls which punk IDs are shown in the Punks frise.
   * - matching: show only matchingOwnedPunks (ownership/filter intersection)
   * - targeted: show the bid target list (punk indices)
   * - auto: matching first, fallback to targeted when matching is empty
   */
  @Input() punkIdsDisplayMode: 'auto' | 'matching' | 'targeted' = 'auto';
  /** When true, display a Status column (e.g. pending, invalid). Useful when showing all bids regardless of status. */
  @Input() showStatusColumn: boolean = false;
  /** When false, hide Expires column (e.g. PunksMarket on-chain bids have no expiry). */
  @Input() showExpiresColumn: boolean = true;
  /** When set, pagination is server-side: total count from server, page change emitted to parent. */
  @Input() totalCount?: number;
  /** Current page when using server-side pagination (1-based). */
  @Input() serverSideCurrentPage: number = 1;
  @Input() canAcceptBidFn?: (bid: StashBid) => boolean;
  @Input() canCancelBidFn?: (bid: StashBid) => boolean;
  @Output() acceptBid = new EventEmitter<StashBid>();
  @Output() cancelBid = new EventEmitter<StashBid>();
  /** Emitted when user clicks "Copy bid" to create a new bid pre-filled from the selected one. */
  @Output() copyBid = new EventEmitter<StashBid>();
  /** Emitted when user changes page in server-side pagination mode. */
  @Output() pageChange = new EventEmitter<number>();
  /** Emitted when filters or sort change in server-side mode (totalCount is set). */
  @Output() filtersChange = new EventEmitter<BidFilterState>();

  processedBids: StashBid[] = [];
  currentPage = 1;
  totalPages = 1;

  // Sort: column key and direction (used when showFilters or sortable headers)
  sortBy: 'amount' | 'expires' | 'bidder' | 'status' | 'source' | 'created' = 'amount';
  sortOrder: 'asc' | 'desc' = 'desc';

  // Filters (used when showFilters)
  filterStatus = '';
  filterSource = '';
  filterBidder = '';

  /** Unique source values from bids (for filter dropdown when showFilters) */
  uniqueSources: string[] = [];
  
  // Bid details modal state
  bidDetailsModalActive: boolean = false;
  selectedBidForDetails: StashBid | null = null;

  // Bid log modal state
  bidLogModalActive: boolean = false;
  selectedBidForLog: StashBid | null = null;

  /** Bid ID that was just copied (show checkmark for ~1s) */
  copiedBidId: string | null = null;
  private copyFeedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  /** Whether the connected wallet is an admin (can see View bid log button). */
  showViewBidLogButton = false;
  private destroy$ = new Subject<void>();

  private readonly STASH_FACTORY_ADDRESS = '0x000000000000A6fA31F5fC51c1640aAc76866750';
  private readonly STASH_INIT_CODE_HASH = '0xe5e58e5bf515959cfde70a49827f414a27af4ee79b57e5fd8cf060d9130fa9b9';
  private readonly STASH_SALT_SUFFIX = BigInt('0x84575f01b760066c746145c8');  

  protected readonly environment = environment;
  public readonly dataSvc: DataService;
  theme$: Observable<string>;
  private currentTheme: string = 'dark';

  constructor(
    private dataService: DataService,
    private bidTagsService: BidTagsService,
    private web3Svc: Web3Service,
    private stateSvc: StateService,
    public themeService: ThemeService
  ) {
    this.dataSvc = this.dataService;
    this.theme$ = this.themeService.theme$;
  }

  ngOnInit(): void {
    this.processBids();
    this.updatePagination();

    // Re-process bids when theme changes so pre-computed platform icons stay in sync
    this.theme$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((theme) => {
        this.currentTheme = theme;
        this.processBids();
      });

    this.stateSvc.walletAddress$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe((walletAddress) => {
        const adminWallets = environment.adminWallets ?? [];
        const normalized = (walletAddress || '').toLowerCase();
        this.showViewBidLogButton =
          !!normalized &&
          adminWallets.some((addr) => addr.toLowerCase() === normalized);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.copyFeedbackTimeout != null) {
      clearTimeout(this.copyFeedbackTimeout);
    }
  }

  ngOnChanges(): void {
    this.processBids();
    this.updatePagination();
  }

  private get isServerSide(): boolean {
    return this.totalCount != null;
  }

  private emitFiltersChange(): void {
    this.filtersChange.emit({
      sortBy: this.sortBy === 'created' ? 'created_at' : this.sortBy,
      sortOrder: this.sortOrder,
      filterStatus: this.filterStatus,
      filterSource: this.filterSource,
      filterBidder: this.filterBidder,
    });
  }

  private processBids(): void {
    const enriched = this.bids.map((bid: any) => {
      // Skip processing for legacy bids - they already have all needed properties
      if (bid.isLegacyBid || bid.bidType === 'legacy') {
        const uiStatus = this.mapBidStatusForUi(bid.status, bid.log);
        return {
          ...bid,
          bidTypeLabel: bid.bidTypeLabel || nativeBidTypeLabel,
          _platformIcon: null,
          _platformTooltip: null,
          stash_funded: 1,
          uiStatusLabel: uiStatus.label,
          uiStatusKind: uiStatus.kind,
          displayPunkIds: [],
          showCloneBidButton: false,
        };
      }

      const isPunksmarketRow =
        bid.platform === 'punksmarket' ||
        !!bid.punksmarketBidId ||
        !!bid.punksmarketIndexerBid;
      const pmRaw = bid.punksmarketIndexerBid as PunksmarketIndexerBid | undefined;
      const classified =
        isPunksmarketRow && pmRaw?.bidId
          ? classifyPunksmarketIndexerBid(pmRaw)
          : null;

      const decodedTags = classified
        ? classified.display.tags
        : isPunksmarketRow && Array.isArray(bid.decodedTags)
          ? bid.decodedTags
          : this.bidTagsService.decodeCollectionBidTags(bid);
      const bidType = classified
        ? classified.bidType
        : isPunksmarketRow && bid.bidType && bid.bidType !== 'punksmarket'
          ? bid.bidType
          : isPunksmarketRow
            ? (bid.bidType ?? 'collection')
            : this.bidTagsService.getCollectionBidType(bid, decodedTags);
      const stashAddress = this.computeStashAddress(bid.bidder_address);
      
      // Extract owner address for owner bids
      let ownerAddress: string | undefined;
      if (bidType === 'owner' && decodedTags.length > 0) {
        const ownerTag = decodedTags[0];
        const match = ownerTag.match(/^Owner:\s*(0x[a-fA-F0-9]{40})$/i);
        if (match) {
          ownerAddress = match[1].toLowerCase();
        }
      }

      // Extract punk IDs
      let punkIds: number[] = [];
      try {
        if (bid.selected_punk_ids) {
          punkIds = Array.isArray(bid.selected_punk_ids)
            ? bid.selected_punk_ids
            : typeof bid.selected_punk_ids === 'string'
            ? bid.selected_punk_ids.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id))
            : [];
        } else if (bid.punkIndices) {
          punkIds = Array.isArray(bid.punkIndices) ? bid.punkIndices : [];
        } else if (bid.punk_indices) {
          if (typeof bid.punk_indices === 'string') {
            punkIds = JSON.parse(bid.punk_indices);
          } else if (Array.isArray(bid.punk_indices)) {
            punkIds = bid.punk_indices;
          }
        }
      } catch (error) {
        console.error('Error parsing punk indices:', error);
      }

      const firstPunkId = Array.isArray(punkIds) && punkIds.length > 0 ? punkIds[0] : undefined;
      const punkIndicesCount = bid.punk_indices_count != null ? Number(bid.punk_indices_count) : (Array.isArray(punkIds) ? punkIds.length : 0);

      // Pre-compute platform icon and tooltip to avoid method calls in template
      const enrichedBid: StashBid = {
        ...bid,
        decodedTags,
        bidType,
        stashAddress,
        ownerAddress,
        firstPunkId,
        punkIds, // Store punk IDs for details modal
        punkIndices: Array.isArray(punkIds) ? punkIds : [],
        punk_indices_count: punkIndicesCount,
        matchingOwnedPunks:
          bid.matchingOwnedPunks ??
          (Array.isArray((bid as any).matching_owned_punks) ? (bid as any).matching_owned_punks : undefined) ??
          punkIds,
        bidTypeLabel:
          isPunksmarketRow
            ? classified?.bidTypeLabel ??
              (typeof bid.bidTypeLabel === 'string' ? bid.bidTypeLabel : onChainBidTypeLabelTitle)
            : bidType === 'owner'
              ? 'Owner Bid'
              : bidType === 'custom'
                ? 'Custom Bid'
                : bidType === 'trait'
                  ? 'Trait Bid'
                  : bid.bidTypeLabel ?? 'Collection Bid',
        stash_funded: normalizeStashFundedForUi(bid.stash_funded),
      };
      const uiStatus = this.mapBidStatusForUi(bid.status, bid.log);
      enrichedBid.uiStatusLabel = uiStatus.label;
      enrichedBid.uiStatusKind = uiStatus.kind;
      enrichedBid._platformIcon = this.computePlatformIcon(enrichedBid, this.currentTheme);
      enrichedBid._platformTooltip = this.computePlatformTooltip(enrichedBid);
      enrichedBid.displayPunkIds = this.computeDisplayPunkIds(enrichedBid);
      enrichedBid.showCloneBidButton = !isPunksmarketRow && enrichedBid.bidType !== 'punksmarket';
      return enrichedBid;
    });

    // Unique sources for filter dropdown
    this.uniqueSources = [...new Set(enriched.map((b: any) => b.source).filter(Boolean))].sort();

    if (this.isServerSide || this.preserveServerOrder) {
      this.processedBids = enriched;
      return;
    }

    // Client-side filters
    let filtered = enriched;
    if (this.filterStatus?.trim()) {
      const status = this.filterStatus.trim().toLowerCase();
      filtered = filtered.filter((b: any) => (b.status || '').toLowerCase() === status);
    }
    if (this.filterSource?.trim()) {
      const source = this.filterSource.trim().toLowerCase();
      filtered = filtered.filter((b: any) => (b.source || '').toLowerCase().includes(source));
    }
    if (this.filterBidder?.trim()) {
      const q = this.filterBidder.trim().toLowerCase();
      filtered = filtered.filter((b: any) => (b.bidder_address || '').toLowerCase().includes(q));
    }

    // Client-side sort
    const order = this.sortOrder === 'asc' ? 1 : -1;
    this.processedBids = [...filtered].sort((a: any, b: any) => {
      let cmp = 0;
      switch (this.sortBy) {
        case 'amount':
          cmp = (Number(a.bid_amount_eth || 0) - Number(b.bid_amount_eth || 0));
          break;
        case 'expires':
          cmp = (new Date(a.expires_at || 0).getTime() - new Date(b.expires_at || 0).getTime());
          break;
        case 'bidder':
          cmp = (a.bidder_address || '').localeCompare(b.bidder_address || '');
          break;
        case 'status':
          cmp = (a.status || '').localeCompare(b.status || '');
          break;
        case 'source':
          cmp = (a.source || '').localeCompare(b.source || '');
          break;
        case 'created':
          cmp = (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
          break;
        default:
          cmp = (Number(a.bid_amount_eth || 0) - Number(b.bid_amount_eth || 0));
      }
      return cmp * order;
    });
  }

  private computeDisplayPunkIds(bid: StashBid): number[] {
    const matching = this.normalizePunkIdList(bid.matchingOwnedPunks);
    const targeted = this.normalizePunkIdList(bid.punkIds ?? bid.punkIndices);

    if (this.punkIdsDisplayMode === 'matching') {
      return matching;
    }
    if (this.punkIdsDisplayMode === 'targeted') {
      return targeted;
    }
    return matching.length > 0 ? matching : targeted;
  }

  private normalizePunkIdList(input: unknown): number[] {
    if (!Array.isArray(input)) {
      return [];
    }
    return input
      .map((v) => (typeof v === 'string' ? Number.parseInt(v, 10) : Number(v)))
      .filter((id) => Number.isInteger(id) && id >= 0);
  }

  /** Called when filter inputs change (from template). */
  applyFiltersAndSort(): void {
    if (this.isServerSide) {
      this.emitFiltersChange();
      return;
    }
    this.processBids();
    this.updatePagination();
    this.currentPage = 1;
  }

  clearFilters(): void {
    this.filterStatus = '';
    this.filterSource = '';
    this.filterBidder = '';
    this.applyFiltersAndSort();
  }

  /** True when the table should render the Expires column (Stash / legacy only). */
  get displayExpiresColumn(): boolean {
    if (!this.showExpiresColumn) {
      return false;
    }
    return this.bids.some((b) => !this.isPunksmarketBid(b));
  }

  isPunksmarketBid(bid: StashBid): boolean {
    return bid.platform === 'punksmarket' || bid.bidType === 'punksmarket';
  }

  setSort(column: 'amount' | 'expires' | 'bidder' | 'status' | 'source' | 'created'): void {
    if (column === 'expires' && !this.displayExpiresColumn) {
      return;
    }
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = column === 'amount' || column === 'expires' || column === 'created' ? 'desc' : 'asc';
    }
    if (this.isServerSide) {
      this.emitFiltersChange();
      return;
    }
    this.processBids();
    this.updatePagination();
    this.currentPage = 1;
  }

  getSortIcon(column: 'amount' | 'expires' | 'bidder' | 'status' | 'source' | 'created'): string {
    if (this.sortBy !== column) return '↕';
    return this.sortOrder === 'asc' ? '↑' : '↓';
  }

  private computeStashAddress(ownerAddress: string | null | undefined): string | null {
    const salt = this.computeStashSalt(ownerAddress);
    if (!salt) {
      return null;
    }
    const payload = `0xff${this.STASH_FACTORY_ADDRESS.slice(2)}${salt.slice(2)}${this.STASH_INIT_CODE_HASH.slice(2)}` as `0x${string}`;
    const hash = keccak256(payload);
    return `0x${hash.slice(-40)}`;
  }

  private computeStashSalt(ownerAddress: string | null | undefined): `0x${string}` | null {
    const normalized = this.normalizeHexAddress(ownerAddress);
    if (!normalized) {
      return null;
    }
    try {
      const ownerValue = BigInt(normalized);
      const salt = (ownerValue << 96n) | this.STASH_SALT_SUFFIX;
      return `0x${salt.toString(16).padStart(64, '0')}` as `0x${string}`;
    } catch {
      return null;
    }
  }


  private normalizeHexAddress(address: string | null | undefined): string | null {
    if (!address) {
      return null;
    }
    const trimmed = address.trim();
    if (!trimmed) {
      return null;
    }
    const prefixed = trimmed.startsWith('0x') ? trimmed : `0x${trimmed}`;
    return /^0x[0-9a-fA-F]{40}$/.test(prefixed) ? prefixed : null;
  }
  
  private updatePagination(): void {
    if (this.totalCount != null && this.totalCount >= 0) {
      this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.itemsPerPage));
    } else {
      this.totalPages = Math.ceil(this.processedBids.length / this.itemsPerPage);
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = this.totalPages;
      }
    }
  }

  getPaginatedBids(): StashBid[] {
    if (this.totalCount != null) {
      return this.processedBids;
    }
    // Item-view "all bids" modal uses showPagination=false but still needs every row (sorted by amount).
    if (!this.showPagination) {
      return this.processedBids;
    }
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.processedBids.slice(start, end);
  }

  getDisplayedCurrentPage(): number {
    return this.totalCount != null ? this.serverSideCurrentPage : this.currentPage;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    if (this.totalCount != null) {
      this.pageChange.emit(page);
    } else {
      this.currentPage = page;
    }
  }

  /**
   * Returns pagination items: first 3 pages, optional "...", 3 pages around current, optional "...", last 3 pages.
   * For 10 or fewer total pages, returns all page numbers.
   */
  getPaginationItems(): (number | 'ellipsis')[] {
    const total = this.totalPages;
    if (total <= 10) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const current = this.getDisplayedCurrentPage();
    const first = [1, 2, 3];
    const last = [total - 2, total - 1, total];
    const middle = [current - 1, current, current + 1].filter(p => p >= 1 && p <= total);
    const allPages = [...new Set([...first, ...middle, ...last])].sort((a, b) => a - b);
    const result: (number | 'ellipsis')[] = [];
    let prev = 0;
    for (const p of allPages) {
      if (p - prev > 1) {
        result.push('ellipsis');
      }
      result.push(p);
      prev = p;
    }
    return result;
  }

  copyBidJson(bid: StashBid, event: Event): void {
    event.stopPropagation();
    navigator.clipboard.writeText(JSON.stringify(bid, null, 2));
  }

  copyBidId(bid: StashBid, event: Event): void {
    event.stopPropagation();
    if (bid?.id) {
      navigator.clipboard.writeText(bid.id);
      if (this.copyFeedbackTimeout != null) {
        clearTimeout(this.copyFeedbackTimeout);
      }
      this.copiedBidId = bid.id;
      this.copyFeedbackTimeout = setTimeout(() => {
        this.copiedBidId = null;
        this.copyFeedbackTimeout = null;
      }, 1000);
    }
  }

  onCopyBid(bid: StashBid): void {
    this.copyBid.emit(bid);
  }

  canAcceptBid(bid: StashBid): boolean {
    if (!this.showAcceptButton) {
      return false;
    }
    if (this.canAcceptBidFn) {
      return this.canAcceptBidFn(bid);
    }
    return false;
  }

  onAcceptBid(bid: StashBid): void {
    // Always emit the event - let the parent component handle confirmation logic
    // This allows different parents to implement their own confirmation logic
    this.acceptBid.emit(bid);
  }

  canCancelBid(bid: StashBid): boolean {
    if (!this.showCancelButton) {
      return false;
    }
    if (this.canCancelBidFn) {
      return this.canCancelBidFn(bid);
    }
    return true;
  }

  onCancelBid(bid: StashBid): void {
    this.cancelBid.emit(bid);
  }

  onShowBidDetails(bid: StashBid): void {
    // For legacy bids, open Etherscan link using transaction hash
    if ((bid.isLegacyBid || bid.bidType === 'legacy') && bid.legacyBid?.transactionHash) {
      window.open(`https://etherscan.io/tx/${bid.legacyBid.transactionHash}`, '_blank', 'noopener,noreferrer');
      return;
    }
    // For regular bids, open the modal
    this.selectedBidForDetails = bid;
    this.bidDetailsModalActive = true;
  }

  closeBidDetailsModal(): void {
    this.bidDetailsModalActive = false;
    this.selectedBidForDetails = null;
  }

  openBidLogModal(bid: StashBid): void {
    this.selectedBidForLog = bid;
    this.bidLogModalActive = true;
  }

  closeBidLogModal(): void {
    this.bidLogModalActive = false;
    this.selectedBidForLog = null;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.bidLogModalActive) {
      this.closeBidLogModal();
    }
  }

  trackByPunkId(index: number, punkId: number): number {
    return punkId;
  }

  private computePlatformIcon(bid: StashBid, theme: string): string | null {
    if (bid.isLegacyBid || bid.bidType === 'legacy') {
      return null;
    }
    if (bid.platform === 'punksmarket' || bid.bidType === 'punksmarket') {
      return 'assets/punksmarket-v1-icon.svg';
    }

    const source = (bid.source || 'Stash').toLowerCase();
    
    // Map source to platform identifier
    let platform: string | null = null;
    if (source === 'cryptopunks.eth.limo' || source.includes('eth.limo')) {
      platform = 'cryptopunks.eth';
    } else if (source === 'cryptopunks.app' || source.includes('cryptopunks.app')) {
      platform = 'cryptopunks.app';
    }
    
    // No icon for 'Stash' or unknown sources
    if (!platform) {
      return null;
    }

    const themeSuffix = theme === 'dark' ? 'dark' : 'light';
    return `assets/platform-icons/${platform}.${themeSuffix}.svg`;
  }

  private computePlatformTooltip(bid: StashBid): string | null {
    if (bid.isLegacyBid || bid.bidType === 'legacy') {
      return null;
    }
    if (bid.platform === 'punksmarket' || bid.bidType === 'punksmarket') {
      return onChainBidPlatformTooltip;
    }

    const source = (bid.source || 'Stash').toLowerCase();
    
    if (source === 'cryptopunks.eth.limo' || source.includes('eth.limo')) {
      return 'cryptopunks.eth.limo';
    } else if (source === 'cryptopunks.app' || source.includes('cryptopunks.app')) {
      return 'cryptopunks.app';
    }
    
    return null;
  }

  isPunksmarketBidRow(bid: StashBid): boolean {
    return (
      bid.bidType === 'punksmarket' ||
      bid.platform === 'punksmarket' ||
      !!(bid as StashBid & { punksmarketBidId?: string }).punksmarketBidId
    );
  }

  private mapBidStatusForUi(rawStatus?: string | null, rawLog?: string | null): {
    label: string;
    kind: 'active' | 'expired' | 'cancelled' | '';
  } {
    const status = (rawStatus || '').trim().toLowerCase();
    const log = (rawLog || '').toLowerCase();

    if (status === 'pending') {
      return { label: 'Active', kind: 'active' };
    }

    if (status === 'invalid') {
      if (log.includes('expire')) {
        return { label: 'Expired', kind: 'expired' };
      }
      return { label: 'Cancelled', kind: 'cancelled' };
    }

    if (status) {
      const normalized = status.charAt(0).toUpperCase() + status.slice(1);
      return { label: normalized, kind: '' };
    }

    return { label: '-', kind: '' };
  }
}
